"use client";

import React from "react";
import {
  FiSearch,
  FiCopy,
  FiCheck,
  FiCode,
  FiSend,
  FiBookOpen,
} from "react-icons/fi";
import yaml from "js-yaml";

import config from "configs/app";
import styles from "./ApiDocsPage.module.css";

const feature = config.features.restApiDocs;

interface Parameter {
  name: string;
  in: string;
  required?: boolean;
  description?: string;
  schema?: { type?: string; enum?: string[] };
  type?: string;
}

interface Operation {
  summary?: string;
  description?: string;
  operationId?: string;
  tags?: string[];
  parameters?: Parameter[];
  responses?: Record<string, any>;
  requestBody?: any;
}

interface PathItem {
  [method: string]: Operation;
}

interface OpenAPISpec {
  info?: { title?: string; version?: string; description?: string };
  paths?: Record<string, PathItem>;
  tags?: Array<{ name: string; description?: string }>;
}

interface Endpoint {
  method: string;
  path: string;
  operation: Operation;
}

const HTTP_METHODS = ["get", "post", "put", "delete", "patch"];

const METHOD_COLORS: Record<
  string,
  { bg: string; color: string; dot: string }
> = {
  get: { bg: "rgba(59, 130, 246, 0.12)", color: "#3b82f6", dot: "#3b82f6" },
  post: { bg: "rgba(34, 197, 94, 0.12)", color: "#22c55e", dot: "#22c55e" },
  put: { bg: "rgba(245, 158, 11, 0.12)", color: "#f59e0b", dot: "#f59e0b" },
  delete: { bg: "rgba(239, 68, 68, 0.12)", color: "#ef4444", dot: "#ef4444" },
  patch: { bg: "rgba(168, 85, 247, 0.12)", color: "#a855f7", dot: "#a855f7" },
};

/* ===== Request Panel (Right Side) ===== */
const RequestPanel: React.FC<{ endpoint: Endpoint; apiBaseUrl: string }> = ({
  endpoint,
  apiBaseUrl,
}) => {
  const { method, path, operation } = endpoint;
  const [paramValues, setParamValues] = React.useState<Record<string, string>>(
    {},
  );
  const [response, setResponse] = React.useState<{
    status: number;
    data: string;
  } | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [copiedUrl, setCopiedUrl] = React.useState(false);

  const colors = METHOD_COLORS[method] || METHOD_COLORS.get;

  // Reset on endpoint change
  React.useEffect(() => {
    setParamValues({});
    setResponse(null);
  }, [path, method]);

  const buildUrl = () => {
    let url = `${apiBaseUrl}${path}`;
    const queryParams: string[] = [];
    operation.parameters?.forEach((param) => {
      const value = paramValues[param.name];
      if (!value) return;
      if (param.in === "path") {
        url = url.replace(`{${param.name}}`, encodeURIComponent(value));
      } else if (param.in === "query") {
        queryParams.push(`${param.name}=${encodeURIComponent(value)}`);
      }
    });
    return queryParams.length > 0 ? `${url}?${queryParams.join("&")}` : url;
  };

  const handleExecute = async () => {
    setIsLoading(true);
    setResponse(null);
    try {
      const res = await fetch(buildUrl());
      const data = await res.json();
      setResponse({ status: res.status, data: JSON.stringify(data, null, 2) });
    } catch (err: any) {
      setResponse({ status: 0, data: `Error: ${err.message}` });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(buildUrl());
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleCopyCurl = () => {
    const curl = `curl -X '${method.toUpperCase()}' \\\n  '${buildUrl()}' \\\n  -H 'accept: application/json'`;
    navigator.clipboard.writeText(curl);
  };

  return (
    <div className={styles.requestPanel}>
      {/* Header */}
      <div className={styles.rpHeader}>
        <span
          className={styles.rpMethod}
          style={{ background: colors.bg, color: colors.color }}
        >
          {method.toUpperCase()}
        </span>
        <span className={styles.rpPath}>{path}</span>
      </div>

      {operation.summary && (
        <p className={styles.rpSummary}>{operation.summary}</p>
      )}
      {operation.description && (
        <p className={styles.rpDescription}>{operation.description}</p>
      )}

      {/* Parameters */}
      {operation.parameters && operation.parameters.length > 0 && (
        <div className={styles.rpSection}>
          <h4 className={styles.rpSectionTitle}>Parameters</h4>
          <div className={styles.rpParams}>
            {operation.parameters.map((param, i) => (
              <div key={i} className={styles.rpParamRow}>
                <div className={styles.rpParamInfo}>
                  <span className={styles.rpParamName}>
                    {param.name}
                    {param.required && (
                      <span className={styles.rpRequired}>*</span>
                    )}
                  </span>
                  <span className={styles.rpParamMeta}>
                    {param.schema?.type || param.type || "string"} · {param.in}
                  </span>
                </div>
                {param.schema?.enum ? (
                  <select
                    className={styles.rpInput}
                    value={paramValues[param.name] || ""}
                    onChange={(e) =>
                      setParamValues((prev) => ({
                        ...prev,
                        [param.name]: e.target.value,
                      }))
                    }
                  >
                    <option value="">Select...</option>
                    {param.schema.enum.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className={styles.rpInput}
                    placeholder={param.description || param.name}
                    value={paramValues[param.name] || ""}
                    onChange={(e) =>
                      setParamValues((prev) => ({
                        ...prev,
                        [param.name]: e.target.value,
                      }))
                    }
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request URL */}
      <div className={styles.rpSection}>
        <div className={styles.rpUrlHeader}>
          <h4 className={styles.rpSectionTitle}>Request URL</h4>
          <div className={styles.rpUrlActions}>
            <button
              className={styles.rpSmallBtn}
              onClick={handleCopyUrl}
              title="Copy URL"
            >
              {copiedUrl ? <FiCheck size={12} /> : <FiCopy size={12} />}
            </button>
            <button
              className={styles.rpSmallBtn}
              onClick={handleCopyCurl}
              title="Copy cURL"
            >
              cURL
            </button>
          </div>
        </div>
        <code className={styles.rpUrlBox}>{buildUrl()}</code>
      </div>

      {/* Execute */}
      <button
        className={styles.rpExecuteBtn}
        onClick={handleExecute}
        disabled={isLoading}
      >
        <FiSend size={14} />
        {isLoading ? "Sending..." : "Send Request"}
      </button>

      {/* Response */}
      {response && (
        <div className={styles.rpSection}>
          <div className={styles.rpResponseHeader}>
            <h4 className={styles.rpSectionTitle}>Response</h4>
            <span
              className={`${styles.rpStatusBadge} ${response.status >= 200 && response.status < 300 ? styles.rpStatusOk : styles.rpStatusErr}`}
            >
              {response.status || "ERR"}
            </span>
          </div>
          <pre className={styles.rpResponseCode}>{response.data}</pre>
        </div>
      )}
    </div>
  );
};

/* ===== Main Page ===== */
const ApiDocsPage: React.FC = () => {
  const [spec, setSpec] = React.useState<OpenAPISpec | null>(null);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedTag, setSelectedTag] = React.useState<string | null>(null);
  const [selectedEndpoint, setSelectedEndpoint] =
    React.useState<Endpoint | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [copied, setCopied] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState<
    Record<string, boolean>
  >({});

  const apiBaseUrl = `${config.api.endpoint}/api/v2`;

  React.useEffect(() => {
    if (!feature.isEnabled) return;
    fetch(feature.specUrl)
      .then((res) => res.text())
      .then((text) => {
        let parsed: OpenAPISpec;
        try {
          parsed = JSON.parse(text);
        } catch {
          parsed = yaml.load(text) as OpenAPISpec;
        }
        setSpec(parsed);

        // Auto-select first endpoint
        if (parsed?.paths) {
          for (const [path, pathItem] of Object.entries(parsed.paths)) {
            for (const method of HTTP_METHODS) {
              if (pathItem[method]) {
                setSelectedEndpoint({
                  method,
                  path,
                  operation: pathItem[method],
                });
                return;
              }
            }
          }
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const handleCopyBase = React.useCallback(() => {
    navigator.clipboard.writeText(apiBaseUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [apiBaseUrl]);

  if (!feature.isEnabled) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <FiBookOpen size={32} />
          <h1 className={styles.title}>REST API Documentation</h1>
          <p>API documentation is not enabled.</p>
        </div>
      </div>
    );
  }

  // Collect endpoints
  const endpoints: Endpoint[] = [];
  if (spec?.paths) {
    Object.entries(spec.paths).forEach(([path, pathItem]) => {
      HTTP_METHODS.forEach((method) => {
        if (pathItem[method])
          endpoints.push({ method, path, operation: pathItem[method] });
      });
    });
  }

  const allTags = Array.from(
    new Set(endpoints.flatMap((e) => e.operation.tags || ["Other"])),
  );

  const filtered = endpoints.filter((e) => {
    const matchSearch =
      !searchTerm ||
      e.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (e.operation.summary || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchTag =
      !selectedTag || (e.operation.tags || ["Other"]).includes(selectedTag);
    return matchSearch && matchTag;
  });

  const grouped: Record<string, Endpoint[]> = {};
  filtered.forEach((e) => {
    const tag = (e.operation.tags || ["Other"])[0];
    if (!grouped[tag]) grouped[tag] = [];
    grouped[tag].push(e);
  });

  const toggleGroup = (tag: string) => {
    setSidebarCollapsed((prev) => ({ ...prev, [tag]: !prev[tag] }));
  };

  const isSelected = (e: Endpoint) =>
    selectedEndpoint?.method === e.method && selectedEndpoint?.path === e.path;

  return (
    <div className={styles.container}>
      {/* Top Bar */}
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <FiCode size={20} className={styles.topBarIcon} />
          <h1 className={styles.topBarTitle}>REST API</h1>
          <div className={styles.baseUrlChip} onClick={handleCopyBase}>
            <code>{apiBaseUrl}</code>
            {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
          </div>
        </div>
        <div className={styles.topBarRight}>
          {allTags.length > 1 && (
            <div className={styles.topBarTags}>
              <button
                className={`${styles.tagBtn} ${!selectedTag ? styles.tagBtnActive : ""}`}
                onClick={() => setSelectedTag(null)}
              >
                All
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  className={`${styles.tagBtn} ${selectedTag === tag ? styles.tagBtnActive : ""}`}
                  onClick={() =>
                    setSelectedTag(selectedTag === tag ? null : tag)
                  }
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
          <div className={styles.topBarSearch}>
            <FiSearch size={16} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search endpoints..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className={styles.searchClear}
                onClick={() => setSearchTerm("")}
              >
                &times;
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.skeletonLayout}>
          <div className={styles.skeletonSidebar}>
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className={styles.skeleton}
                style={{
                  width: `${60 + Math.random() * 30}%`,
                  height: "1.25rem",
                  marginBottom: "0.5rem",
                }}
              />
            ))}
          </div>
          <div className={styles.skeletonMain}>
            <div
              className={styles.skeleton}
              style={{ width: "40%", height: "1.5rem", marginBottom: "1rem" }}
            />
            <div
              className={styles.skeleton}
              style={{
                width: "100%",
                height: "2.5rem",
                marginBottom: "0.75rem",
              }}
            />
            <div
              className={styles.skeleton}
              style={{
                width: "100%",
                height: "2.5rem",
                marginBottom: "0.75rem",
              }}
            />
            <div
              className={styles.skeleton}
              style={{ width: "100%", height: "2.5rem" }}
            />
          </div>
        </div>
      ) : (
        <div className={styles.layout}>
          {/* Sidebar */}
          <aside className={styles.sidebar}>
            <nav className={styles.sidebarNav}>
              {Object.entries(grouped).map(([tag, eps]) => (
                <div key={tag} className={styles.sidebarGroup}>
                  <button
                    className={styles.sidebarGroupTitle}
                    onClick={() => toggleGroup(tag)}
                  >
                    <span>{tag}</span>
                    <span className={styles.sidebarGroupCount}>
                      {eps.length}
                    </span>
                  </button>
                  {!sidebarCollapsed[tag] && (
                    <div className={styles.sidebarItems}>
                      {eps.map((e, i) => {
                        const colors =
                          METHOD_COLORS[e.method] || METHOD_COLORS.get;
                        return (
                          <button
                            key={`${e.method}-${e.path}-${i}`}
                            className={`${styles.sidebarItem} ${isSelected(e) ? styles.sidebarItemActive : ""}`}
                            onClick={() => setSelectedEndpoint(e)}
                          >
                            <span
                              className={styles.sidebarMethod}
                              style={{ color: colors.color }}
                            >
                              {e.method.toUpperCase()}
                            </span>
                            <span className={styles.sidebarPath}>{e.path}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              {filtered.length === 0 && (
                <div className={styles.sidebarEmpty}>No endpoints found</div>
              )}
            </nav>
          </aside>

          {/* Main Content */}
          <main className={styles.main}>
            {selectedEndpoint ? (
              <RequestPanel
                endpoint={selectedEndpoint}
                apiBaseUrl={apiBaseUrl}
              />
            ) : (
              <div className={styles.placeholder}>
                <FiCode size={48} />
                <h2>Select an endpoint</h2>
                <p>
                  Choose an API endpoint from the sidebar to view details and
                  send requests.
                </p>
                <div className={styles.placeholderStats}>
                  <span>{endpoints.length} endpoints</span>
                  <span>{allTags.length} categories</span>
                </div>
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default ApiDocsPage;
